import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Grid,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { DeleteSweep } from '@mui/icons-material';
import PendingPosts from '../components/socialMedia/PendingPosts';
import ContentGenerator from '../components/socialMedia/ContentGenerator';
import ScheduleManager from '../components/socialMedia/ScheduleManager';
import PostHistory from '../components/socialMedia/PostHistory';
import SocialAnalytics from '../components/socialMedia/SocialAnalytics';
import TemplateManager from '../components/socialMedia/TemplateManager';
import { socialMediaApi } from '../api/socialMediaApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`social-media-tabpanel-${index}`}
      aria-labelledby={`social-media-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const SocialMedia: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAlert = (type: 'success' | 'error' | 'info', message: string) => {
    setAlertMessage({ type, message });
    setTimeout(() => setAlertMessage(null), 5000);
  };

  const handleDeleteAllUnposted = async () => {
    try {
      setDeleting(true);
      const result = await socialMediaApi.deleteAllUnpostedContent();
      setDeleteDialogOpen(false);
      handleAlert('success', result.message || `Deleted ${result.deletedCount} unposted items`);
    } catch (error) {
      console.error('Failed to delete unposted content:', error);
      handleAlert('error', 'Failed to delete unposted content');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Social Media Management
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweep />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete All Unposted
        </Button>
      </Box>

      {alertMessage && (
        <Alert severity={alertMessage.type} sx={{ mb: 2 }} onClose={() => setAlertMessage(null)}>
          {alertMessage.message}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Pending Approval" />
          <Tab label="Generate Content" />
          <Tab label="Schedule" />
          <Tab label="Post History" />
          <Tab label="Analytics" />
          <Tab label="Templates" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <PendingPosts onAlert={handleAlert} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ContentGenerator onAlert={handleAlert} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ScheduleManager onAlert={handleAlert} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <PostHistory onAlert={handleAlert} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <SocialAnalytics />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <TemplateManager onAlert={handleAlert} />
      </TabPanel>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete All Unposted Content</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all social media content that has not been posted yet.
            This includes pending, approved, failed, rejected, and cancelled posts.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllUnposted}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : undefined}
          >
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SocialMedia;